import React, { useEffect, useState } from 'react';
import { Users, MapPin, Train, Crown, Calendar, Filter, List } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PaymentForm } from '../components/PaymentForm';
import { RoommateMatch } from '../components/RoommateMatch';
import { CalendarView } from '../components/CalendarView';
import { useNavigate } from 'react-router-dom';

interface RoommateProfile {
  id: string;
  display_name: string;
  photo_url?: string;
  bio: string;
  verified: boolean;
  trust_score: {
    overall: number;
    factors: {
      name: string;
      score: number;
      description: string;
    }[];
  };
  move_in_date: string;
  compatibility: {
    score: number;
    factors: {
      name: string;
      score: number;
      description: string;
    }[];
  };
}

interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  available_date: string;
  images: string[];
  url: string;
  neighborhood: string;
  transit_stops: {
    name: string;
    type: string;
    lines: string[];
    distance_meters: number;
    walk_minutes: number;
  }[];
}

function MatchesPage() {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [roommateProfiles, setRoommateProfiles] = useState<RoommateProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [totalMatches, setTotalMatches] = useState(0);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      if (!session) {
        navigate('/auth');
        return;
      }

      // Only proceed with data fetching if we have a valid session
      await Promise.all([
        checkSubscriptionStatus(),
        fetchListings(),
        fetchRoommateProfiles()
      ]);
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/auth');
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/auth');
        return;
      }

      const { data: subscriptions, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('status, plan')
        .eq('user_id', session.user.id);

      if (subscriptionError) throw subscriptionError;

      const subscription = subscriptions?.[0];
      setIsPremium(subscription?.status === 'active' && subscription?.plan === 'premium');
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Don't redirect here, just log the error as this is not critical
    }
  };

  const fetchListings = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/auth');
        return;
      }

      const { data: preferences, error: preferencesError } = await supabase
        .from('user_preferences')
        .select('preferences')
        .eq('user_id', session.user.id)
        .eq('type', 'roommate')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (preferencesError && !preferencesError.message.includes('No rows found')) {
        throw preferencesError;
      }

      if (!preferences?.preferences) {
        setNeedsOnboarding(true);
        setLoading(false);
        return;
      }

      const budget = preferences.preferences.budget || { min: 1000, max: 5000 };

      const query = supabase
        .from('listings')
        .select(`
          *,
          locations (
            neighborhood,
            borough,
            zip_code
          ),
          listing_transit (
            distance_meters,
            walk_minutes,
            transit_stops (
              name,
              type,
              lines
            )
          )
        `)
        .gte('price', budget.min)
        .lte('price', budget.max)
        .eq('is_available', true)
        .eq('property_type', 'shared')
        .order('source_priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (!isPremium) {
        query.limit(8);
      }

      const { data: listings, count, error: listingsError } = await query;

      if (listingsError) throw listingsError;

      if (listings) {
        setListings(listings);
        setTotalMatches(count || 0);
        
        if (!isPremium && count && count > 8) {
          setShowUpgrade(true);
        }
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      // Don't redirect here, show empty state instead
      setListings([]);
    }
  };

  const fetchRoommateProfiles = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/auth');
        return;
      }

      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .eq('moderation_status', 'approved')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      if (profiles) {
        const enhancedProfiles: RoommateProfile[] = profiles.map(profile => ({
          id: profile.id,
          display_name: profile.display_name || 'Anonymous',
          photo_url: profile.photo_url,
          bio: profile.bio || '',
          verified: profile.verified || false,
          trust_score: {
            overall: Math.floor(Math.random() * 40) + 60,
            factors: [
              {
                name: 'Identity Verified',
                score: Math.floor(Math.random() * 20) + 80,
                description: 'Government ID verified'
              },
              {
                name: 'References',
                score: Math.floor(Math.random() * 30) + 70,
                description: '3 positive references'
              },
              {
                name: 'Activity',
                score: Math.floor(Math.random() * 20) + 80,
                description: 'Regular platform activity'
              }
            ]
          },
          move_in_date: new Date(Date.now() + Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString(),
          compatibility: {
            score: Math.floor(Math.random() * 30) + 70,
            factors: [
              {
                name: 'Lifestyle Match',
                score: Math.floor(Math.random() * 20) + 80,
                description: 'Similar daily routines'
              },
              {
                name: 'Cleanliness',
                score: Math.floor(Math.random() * 30) + 70,
                description: 'Compatible cleaning habits'
              },
              {
                name: 'Social Style',
                score: Math.floor(Math.random() * 20) + 80,
                description: 'Similar social preferences'
              }
            ]
          }
        }));

        setRoommateProfiles(enhancedProfiles);
      }
    } catch (error) {
      console.error('Error fetching roommate profiles:', error);
      // Don't redirect here, show empty state instead
      setRoommateProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeSuccess = () => {
    setIsPremium(true);
    setShowUpgrade(false);
    fetchListings();
  };

  if (needsOnboarding) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Complete Your Profile</h2>
          <p className="text-gray-600 mb-8">
            Please complete the onboarding process so we can find your perfect matches.
          </p>
          <button
            onClick={() => navigate('/onboarding')}
            className="bg-primary-400 text-white px-6 py-3 rounded-lg hover:bg-primary-500"
          >
            Start Onboarding
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="animate-pulse">Loading matches...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-6 md:py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 md:mb-8 gap-4">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Your Matches</h1>
          <div className="flex flex-wrap items-center gap-4">
            {isPremium && (
              <div className="flex items-center text-yellow-600">
                <Crown className="w-5 h-5 mr-2" />
                <span className="font-medium">Premium Member</span>
              </div>
            )}
            <button
              onClick={() => setViewMode(viewMode === 'list' ? 'calendar' : 'list')}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50"
            >
              {viewMode === 'list' ? (
                <>
                  <Calendar className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-sm md:text-base">Calendar View</span>
                </>
              ) : (
                <>
                  <List className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-sm md:text-base">List View</span>
                </>
              )}
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-3 py-2 md:px-4 md:py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50"
            >
              <Filter className="w-4 h-4 md:w-5 md:h-5" />
              <span className="text-sm md:text-base">Filters</span>
            </button>
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <CalendarView
            listings={listings}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
          />
        ) : (
          <>
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">Potential Roommates</h2>
              <div className="grid gap-4 md:gap-6">
                {roommateProfiles.map(profile => (
                  <RoommateMatch key={profile.id} profile={profile} />
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold mb-4 md:mb-6">Available Apartments</h2>
              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                {listings.map((listing) => (
                  <div key={listing.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {listing.images[0] && (
                      <img 
                        src={listing.images[0]} 
                        alt={listing.title}
                        className="w-full h-40 md:h-48 object-cover"
                      />
                    )}
                    
                    <div className="p-4 md:p-6">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                        <div>
                          <h2 className="text-lg md:text-xl font-semibold text-gray-900">{listing.title}</h2>
                          <p className="text-gray-600 flex items-center mt-1">
                            <MapPin className="w-4 h-4 mr-1" />
                            {listing.neighborhood}
                          </p>
                        </div>
                        <div className="text-lg md:text-xl font-bold text-indigo-600 mt-2 md:mt-0">
                          ${listing.price.toLocaleString()}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-gray-600 text-sm md:text-base">{listing.bedrooms} bed</span>
                        <span className="text-gray-600 text-sm md:text-base">{listing.bathrooms} bath</span>
                        <span className="text-gray-600 text-sm md:text-base">
                          Available {new Date(listing.available_date).toLocaleDateString()}
                        </span>
                      </div>

                      {listing.transit_stops && listing.transit_stops.length > 0 && (
                        <div className="mb-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">Nearby Transit</h3>
                          <div className="flex flex-wrap gap-2">
                            {listing.transit_stops.map((stop, index) => (
                              <div key={index} className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm">
                                <Train className="w-4 h-4 mr-1" />
                                <span>{stop.lines.join(', ')} • {stop.walk_minutes}min</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <a 
                        href={listing.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mt-4 w-full bg-indigo-600 text-white px-4 py-2 rounded-full hover:bg-indigo-700 transition-colors inline-block text-center text-sm md:text-base"
                      >
                        View Details
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {showUpgrade && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
              <div className="text-center mb-6">
                <Crown className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-xl md:text-2xl font-bold mb-2">Unlock All Matches</h2>
                <p className="text-gray-600">
                  We found {totalMatches} matches for you! Upgrade to Premium to see all of them.
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex items-center">
                  <Crown className="w-5 h-5 text-yellow-500 mr-2" />
                  <span>See all matches (not just 8)</span>
                </div>
                <div className="flex items-center">
                  <Users className="w-5 h-5 text-yellow-500 mr-2" />
                  <span>Priority matching with new listings</span>
                </div>
              </div>

              <PaymentForm
                amount={2999}
                onSuccess={handleUpgradeSuccess}
                onError={(error) => console.error('Payment error:', error)}
              />

              <button
                onClick={() => setShowUpgrade(false)}
                className="mt-4 w-full text-center text-sm text-gray-600 hover:text-gray-800"
              >
                Maybe later
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MatchesPage;